/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { ENoteStatus } from '@blend-types/eclose';
import { NotaryAPI } from '../../api/notary/types';
import { participantAPI } from '../../api/participant';
import { SignAheadDocsStatus } from '../../shared/constants/room';
import { enterpriseColors } from '../../style/colors';
import { ConfirmationModal } from '@components/common/Modals/ConfirmationModal/ConfirmationModal';

export namespace PendingTaskBanner {
  export interface Props {
    roomId: string;
    isNotarySide: boolean;
  }
}

const PendingTaskBanner: React.FC<PendingTaskBanner.Props> = ({ roomId, isNotarySide }) => {
  const [isPendingTaskModalOpen, setIsPendingTaskModalOpen] = useState(false);
  const [eSignPending, setESignPending] = useState(false);
  const [eNotePending, setENotePending] = useState(false);

  const checkAndDisplayPopup = (metadata: NotaryAPI.RoomMetadata | null): void => {
    if (!metadata) {
      return;
    }

    const isENotePending =
      !!metadata.eNoteStatus && metadata.eNoteStatus !== ENoteStatus.COMPLETE && !metadata.signENoteInRON;

    const isESignPending =
      !!metadata.signAheadDocsStatus &&
      metadata.signAheadDocsStatus === SignAheadDocsStatus.PENDING_BORROWER_SIGNATURE &&
      !metadata.sentSignAheadDocsToRON;

    setESignPending(isESignPending);
    setENotePending(isENotePending);

    setIsPendingTaskModalOpen(isENotePending || isESignPending);
  };

  useEffect(() => {
    (async () => {
      const roomMetadata = await participantAPI.rooms.fetchRoomMetadata(roomId);
      checkAndDisplayPopup(roomMetadata as NotaryAPI.RoomMetadata | null);
    })();
  }, [roomId]);

  const getModalContent = () => {
    const tasksMessage = isNotarySide
      ? 'The borrower(s) have not yet completed the following required tasks to close the loan:'
      : 'You have not yet completed the following required tasks to close the loan:';

    const reminderMessage = isNotarySide
      ? 'Please remind the borrowers to finish these task(s) before ending the appointment.'
      : 'Please complete these task(s) before the end of the appointment.';

    return (
      <>
        <p>{tasksMessage}</p>
        <ul>
          {eSignPending && <li>eSign Closing Documents</li>}
          {eNotePending && <li>Sign Promissory Note</li>}
        </ul>
        <p>These task(s) can be completed by viewing {isNotarySide ? 'their' : 'your'} tasks in the online portal.</p>
        <p>
          <strong>{reminderMessage}</strong>
        </p>
      </>
    );
  };

  return (
    <ConfirmationModal
      heading={'Incomplete task'}
      contentLabel={'Incomplete task Modal'}
      isOpen={isPendingTaskModalOpen}
      confirmButtonText={'Continue'}
      handleConfirm={() => setIsPendingTaskModalOpen(false)}
      buttonColor={enterpriseColors.PRIMARY_500}
    >
      {getModalContent()}
    </ConfirmationModal>
  );
};
export default PendingTaskBanner;
